import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';

import { FormHelperText } from "components/form-fields";

import 'components/form-fields/FormFields.css';
import { Button } from 'components/buttons';
import { ShowHide } from 'components/layout/ShowHide';
import { ListingTable } from 'components/listing/ListingTable';
import { parseBytesTo } from 'utils/parseBytesTo';
import { NotificationToaster } from 'components/notifications/NotificationToaster';
import { Icon, Intent, Spinner } from '@blueprintjs/core';
import { NotificationInline } from 'components/notifications';
import { Tooltip } from 'components/tooltip';

export const FormFileUploadStatus = Object.freeze({
    PENDING: "pending",
    UPLOADING: "uploading",
    COMPLETE: "complete",
    FAILED: "failed",
});

const defaultArray = [];

export function FormDragAndDropFileUploader(
    {
        id = null,
        loading = false,
        dangerHelperText = null,
        disabled = false,
        smaller = false,
        headingText = null,
        documents = defaultArray,
        showNewVersionsAsWarning = false,
        showVersions = true,
        versionColumnHeader = "Version",
        sortComparator = null,
        allowedFileTypes = [],
        fixedTableHeight = true,
        versionWarningText = "Some files already exist for the document type with the same file name. Uploading another file will create a new version of the document.",
        maxFileSize,
        onChange,
        onRemoveDocument
    }
) {
    const inputRef = useRef();

    const [isDragging, setIsDragging] = useState(false);
    const [displayList, setDisplayList] = useState([]);
    const [displayHeaders, setDisplayHeaders] = useState([]);

    const handleDragEnter = () => setIsDragging(true);
    const handleDragLeave = (event) => {
        const rect = event.currentTarget.getBoundingClientRect();
        const x = event.clientX;
        const y = event.clientY;

        // Check if the mouse is still inside the drop zone
        if (
            x < rect.left ||
            x > rect.right ||
            y < rect.top ||
            y > rect.bottom
        ) {
            setIsDragging(false);
        }
    }

    const onFilesSelected = (event) => {
        if (event.currentTarget.files && event.currentTarget.files.length > 0) {
            addFiles(event.currentTarget.files);
        }
    }

    // An allowed type entry beginning with "." is matched against the file's extension
    // (tacho files such as .ddd / .vu have no registered MIME type, so file.type is
    // empty); any other entry is matched against the file's MIME type as before.
    const isAllowedFileType = (file) => {
        const fileName = file.name.toLowerCase();
        const fileMimeType = file.type.toLowerCase().trim();

        return allowedFileTypes.some((allowedType) => {
            const normalisedType = allowedType.toLowerCase().trim();

            if (normalisedType.startsWith(".")) {
                return fileName.endsWith(normalisedType);
            }

            return normalisedType === fileMimeType;
        });
    }

    const addFiles = (files) => {
        let duplicateFiles = false;
        let tooLargeFiles = false;
        let invalidTypeFiles = false;

        const newFiles = [];

        for (let file of files) {

            if (documents.find(f => f.name === file.name)) {
                duplicateFiles = true;
                continue;
            }

            if (file.size > maxFileSize) {
                tooLargeFiles = true;
                continue;
            }

            if (!isAllowedFileType(file)) {
                invalidTypeFiles = true;
                continue;
            }

            newFiles.push(file);
        }

        if (duplicateFiles) {
            NotificationToaster.show(Intent.WARNING, "Some files were not added because they are duplicates.");
        }

        if (tooLargeFiles) {
            NotificationToaster.show(Intent.WARNING, `Some files were not added because they exceed the maximum file size of ${parseBytesTo(maxFileSize).auto()}.`);
        }

        if (invalidTypeFiles) {
            NotificationToaster.show(Intent.WARNING, "Some files were not added because they are of an invalid file type.");
        }

        if (newFiles.length > 0) {
            onChange(newFiles);
        }
    }

    const onDrop = (event) => {
        event.preventDefault();
        if (event.dataTransfer.files && event.dataTransfer.files.length > 0 && !disabled) {
            addFiles(event.dataTransfer.files);
        }

        setIsDragging(false);
    }

    const onDragOver = (event) => {
        event.preventDefault();
    }

    useEffect(() => {

        let headers = [
            "File Name",
            versionColumnHeader,
            "File Size",
            "Action"
        ];

        if (!showVersions) {
            headers = headers.filter(h => h !== versionColumnHeader);
        }

        setDisplayHeaders(headers);

        const nameSorter = (a, b) => a?.name?.localeCompare(b?.name) ?? 0;
        const comparator = sortComparator ?? nameSorter;

        setDisplayList([...documents].sort(comparator).map((doc) => {
            let columns = [];

            let displayStatus = null;
            switch (doc.status) {
                case FormFileUploadStatus.PENDING:
                    displayStatus = <Icon key={doc.name} icon="time" />;
                    break;
                case FormFileUploadStatus.UPLOADING:
                    displayStatus = <Spinner key={doc.name} size={16} intent={Intent.PRIMARY} className='file-upload-spinner' />;
                    break;
                case FormFileUploadStatus.COMPLETE:
                    displayStatus = <Icon key={doc.name} intent={Intent.SUCCESS} icon="tick-circle" />;
                    break;
                case FormFileUploadStatus.FAILED:
                    displayStatus = <Tooltip content={doc.statusComment} disabled={(doc.statusComment?.length ?? 0) === 0}><Icon key={doc.name} intent={Intent.DANGER} icon="error" /></Tooltip>;
                    break;
                default:
                    break;
            }

            columns.push(<div className="inline-items" key={doc.name}>
                <div className="inline-item">{displayStatus}</div>
                <div className="inline-item">{doc.name}</div>
            </div>);

            if (showVersions) {
                columns.push(<span key={doc.name} className={classNames({ "bp6-skeleton": !!doc.loadingVersion })}>{doc.version ?? 'Loading...'}</span>);
            }
            columns.push(parseBytesTo(doc.size).auto());

            columns.push(<Button key={doc.name} icon="trash" large={false} disabled={disabled} minimal={true} intent="danger" onClick={() => onRemoveDocument(doc.name)} />);

            return columns;
        }));

    }, [documents, showVersions, versionColumnHeader, sortComparator, disabled]);

    return (
        <>
            <ShowHide
                evaluator={documents.some(f => f.version > 1)}
                show={<div className="spacer-bottom-small">
                    <ShowHide
                        evaluator={showNewVersionsAsWarning}
                        show={
                            <NotificationInline intent={Intent.WARNING} show={true} text={versionWarningText} />
                        }
                        hide={
                            <span className="helper-text">
                                {versionWarningText}
                            </span>
                        }
                    />
                </div>}
            />
            <div className='form-field'>
                <ShowHide
                    evaluator={headingText != null}
                    show={(
                        <h3 className={classNames({ "bp6-skeleton": loading })}>{headingText}</h3>
                    )}
                />
                <div className={classNames('form-field-drag-and-drop-uploader', { 'smaller': smaller }, { 'dragging': isDragging })}
                    onDragOver={onDragOver}
                    onDrop={onDrop}
                    onDragEnter={handleDragEnter}
                    onDragLeave={handleDragLeave}
                    role='group'
                >
                    <span>Drag and drop files to import or </span>
                    <input
                        id={id}
                        disabled={disabled}
                        type={"file"}
                        onChange={onFilesSelected}
                        multiple
                        ref={inputRef}
                    />
                    <Button
                        text="Choose files"
                        onClick={() => inputRef.current.click()}
                        disabled={disabled}
                        loading={loading}
                        intent='primary'
                        large={false}

                    />
                </div>
                <FormHelperText loading={loading} danger={true}>{dangerHelperText}</FormHelperText>
            </div>
            <ShowHide
                evaluator={!!documents.length}
                show={
                    <>
                        <h3>Files to Upload {'(' + displayList.length + ')'}</h3>
                        <ListingTable
                            showPerPage={false}
                            shrinkLastColumn={true}
                            loadingData={false}
                            headers={displayHeaders}
                            pageable={false}
                            totalRecordCount={documents.length}
                            fixedHeight={fixedTableHeight}
                            data={displayList}

                        />
                    </>
                }
            />
        </>
    );
}

FormDragAndDropFileUploader.propTypes = {
    id: PropTypes.string,
    onChange: PropTypes.func.isRequired,
    loading: PropTypes.bool,
    disabled: PropTypes.bool,
    dangerHelperText: PropTypes.node,
    headingText: PropTypes.string,
    documents: PropTypes.arrayOf(
        PropTypes.shape({
            name: PropTypes.string.isRequired,
            size: PropTypes.number.isRequired,
            loadingVersion: PropTypes.bool,
            version: PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.node]),
            status: PropTypes.oneOf(Object.values(FormFileUploadStatus)),
            statusComment: PropTypes.string
        })
    ),
    onRemoveDocument: PropTypes.func.isRequired,
    maxFileSize: PropTypes.number,
    showNewVersionsAsWarning: PropTypes.bool,
    versionWarningText: PropTypes.string,
    allowedFileTypes: PropTypes.arrayOf(PropTypes.string),
    showVersions: PropTypes.bool,
    versionColumnHeader: PropTypes.string,
    sortComparator: PropTypes.func,
    fixedTableHeight: PropTypes.bool
};